import React, { useState, useEffect, useCallback } from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';
import axios from 'axios';
import { debounce } from 'lodash';
import { BACKEND_URL } from '../../utils/backendUrl';


const ComplianceGuardrails = ({ text, studioType = 'content', productIds = [], campaignId = null, onComplianceChange }) => {
  const [complianceResult, setComplianceResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Debounced compliance check (wait 1 second after user stops typing)
  const checkCompliance = useCallback(
    debounce(async (textToCheck) => {
      if (!textToCheck || textToCheck.length < 10) {
        setComplianceResult(null);
        if (onComplianceChange) onComplianceChange(null);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.post(`${BACKEND_URL}/api/v3/studios/compliance-check`, {
          text: textToCheck,
          studio_type: studioType,
          product_ids: productIds,
          campaign_id: campaignId
        });

        setComplianceResult(response.data);
        if (onComplianceChange) onComplianceChange(response.data);
      } catch (err) {
        console.error('Error checking compliance:', err);
      } finally {
        setLoading(false);
      }
    }, 1000),
    [studioType, productIds, campaignId, onComplianceChange]
  );

  useEffect(() => {
    checkCompliance(text);
    // Cleanup debounce on unmount
    return () => checkCompliance.cancel();
  }, [text, checkCompliance]);

  if (!complianceResult && !loading) return null;

  const { status, flagged_phrases, compliance_score } = complianceResult || {};

  // Status color
  const getStatusColor = () => {
    if (status === 'safe') return EMZI_COLORS.success;
    if (status === 'warning') return EMZI_COLORS.warning;
    return EMZI_COLORS.error;
  };

  const getStatusIcon = () => {
    if (status === 'safe') return '✅';
    if (status === 'warning') return '⚠️';
    return '⛔';
  };

  const getStatusText = () => {
    if (status === 'safe') return 'Compliant';
    if (status === 'warning') return 'Review Needed';
    return 'Blocked - Compliance Issues';
  };

  return (
    <div style={{
      marginTop: EMZI_SPACING.sm,
      padding: EMZI_SPACING.md,
      backgroundColor: '#FFFFFF',
      border: `2px solid ${getStatusColor()}`,
      borderRadius: EMZI_RADIUS.lg,
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      {/* Status Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: EMZI_SPACING.sm }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>{getStatusIcon()}</span>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: getStatusColor() }}>
              {getStatusText()}
            </div>
            <div style={{ fontSize: '10px', color: EMZI_COLORS.textMuted }}>
              Compliance Score: {compliance_score || 0}/100
            </div>
          </div>
        </div>
        {loading && (
          <div style={{ fontSize: '10px', color: EMZI_COLORS.textMuted }}>
            Checking...
          </div>
        )}
      </div>

      {/* Flagged Phrases */}
      {flagged_phrases && flagged_phrases.length > 0 && (
        <div>
          <div style={{ fontSize: '11px', fontWeight: '600', color: EMZI_COLORS.textMuted, marginBottom: EMZI_SPACING.xs }}>
            Issues Found ({flagged_phrases.length}):
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: EMZI_SPACING.xs }}>
            {flagged_phrases.map((flagged, index) => (
              <FlaggedPhraseCard key={`item-${index}`} flagged={flagged} />
            ))}
          </div>
        </div>
      )}

      {/* All Clear Message */}
      {status === 'safe' && (!flagged_phrases || flagged_phrases.length === 0) && (
        <div style={{
          padding: EMZI_SPACING.sm,
          backgroundColor: EMZI_COLORS.success + '10',
          borderRadius: EMZI_RADIUS.md,
          fontSize: '11px',
          color: EMZI_COLORS.success
        }}>
          ✓ No compliance issues detected. Content is ready for approval.
        </div>
      )}
    </div>
  );
};

const FlaggedPhraseCard = ({ flagged }) => {
  const [showAlternative, setShowAlternative] = useState(false);

  return (
    <div style={{
      padding: EMZI_SPACING.sm,
      backgroundColor: EMZI_COLORS.error + '10',
      borderLeft: `3px solid ${EMZI_COLORS.error}`,
      borderRadius: EMZI_RADIUS.sm
    }}>
      {/* Flagged Phrase */}
      <div style={{ marginBottom: '6px' }}>
        <div style={{ fontSize: '10px', color: EMZI_COLORS.textMuted, marginBottom: '2px' }}>
          Flagged Phrase:
        </div>
        <div style={{
          fontSize: '12px',
          fontWeight: '600',
          color: EMZI_COLORS.error,
          fontFamily: 'monospace',
          padding: '4px 8px',
          backgroundColor: '#FFF',
          borderRadius: EMZI_RADIUS.sm,
          display: 'inline-block'
        }}>
          "{flagged.phrase}"
        </div>
      </div>

      {/* Category & Severity */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
        <span style={{
          padding: '2px 6px',
          backgroundColor: EMZI_COLORS.error + '20',
          color: EMZI_COLORS.error,
          borderRadius: EMZI_RADIUS.sm,
          fontSize: '9px',
          fontWeight: '600',
          textTransform: 'uppercase'
        }}>
          {flagged.severity || 'High'} Risk
        </span>
        <span style={{
          padding: '2px 6px',
          backgroundColor: EMZI_COLORS.textMuted + '20',
          color: EMZI_COLORS.textMuted,
          borderRadius: EMZI_RADIUS.sm,
          fontSize: '9px',
          fontWeight: '600',
          textTransform: 'uppercase'
        }}>
          {flagged.category || 'Compliance'}
        </span>
      </div>

      {/* Safe Alternative */}
      {flagged.alternative && (
        <div>
          <button
            onClick={() => setShowAlternative(!showAlternative)}
            style={{
              background: 'none',
              border: 'none',
              color: EMZI_COLORS.success,
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
              marginBottom: '4px'
            }}
          >
            {showAlternative ? '▼ Hide' : '▶ Show'} Safe Alternative
          </button>
          {showAlternative && (
            <div style={{
              padding: '8px',
              backgroundColor: EMZI_COLORS.success + '10',
              borderRadius: EMZI_RADIUS.sm,
              fontSize: '11px',
              color: EMZI_COLORS.textPrimary,
              lineHeight: '1.5',
              marginTop: '4px'
            }}>
              <div style={{ fontSize: '9px', color: EMZI_COLORS.success, marginBottom: '4px', fontWeight: '600' }}>
                ✓ SUGGESTED ALTERNATIVE:
              </div>
              {flagged.alternative}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComplianceGuardrails;
